import React, { useState } from 'react';
import { X, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { stcResourceService, CreateStcResourceRequest } from '../../../../../services/stcResourceService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ResourceCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const ResourceCreateModal: React.FC<ResourceCreateModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const [formData, setFormData] = useState<CreateStcResourceRequest>({
        companyNo: user?.companyNo || '001',
        resourceNo: 0,
        description1: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.description1.trim()) {
            setError(t('manager.modules.adminResources.resourceCreate.errorDescriptionRequired', {}, 'Beskrivelse er obligatorisk'));
            return;
        }

        if (formData.resourceNo <= 0) {
            setError(t('manager.modules.adminResources.resourceCreate.errorResourceNoPositive', {}, 'Ressursnummer må være større enn 0'));
            return;
        }

        try {
            setIsSaving(true);
            setError('');
            await stcResourceService.create(formData);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSuccess();
            }, 1200);
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminResources.resourceCreate.errorCreate', {}, 'Feil ved opprettelse av ressurs'));
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            {/* Modal */}
            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">{t('manager.modules.adminResources.resourceCreate.title', {}, 'Ny ressurs')}</h2>
                            <p className="text-sm text-green-100">{t('manager.modules.adminResources.resourceCreate.subtitle', {}, 'Opprett en ny ressurs i lageret')}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">{t('manager.modules.adminResources.resourceCreate.successCreated', {}, 'Ressurs opprettet!')}</p>
                                </div>
                            </div>
                        )}

                        <div>
                            <label htmlFor="resourceNo" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('manager.modules.adminResources.resourceCreate.resourceNoLabel', {}, 'Ressursnummer')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="resourceNo"
                                type="number"
                                value={formData.resourceNo || ''}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    resourceNo: parseInt(e.target.value) || 0
                                }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder={t('manager.modules.adminResources.resourceCreate.resourceNoPlaceholder', {}, 'F.eks. 1, 2, 3...')}
                                required
                                min={1}
                                autoFocus
                            />
                        </div>

                        <div>
                            <label htmlFor="description1" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('common.fields.description', {}, 'Beskrivelse')} <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="description1"
                                type="text"
                                value={formData.description1}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    description1: e.target.value
                                }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder={t('manager.modules.adminResources.resourceCreate.descriptionPlaceholder', {}, 'Beskrivelse av ressursen')}
                                required
                            />
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {t('manager.modules.adminResources.resourceCreate.creating', {}, 'Oppretter...')}
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        {t('manager.modules.adminResources.resourceCreate.createButton', {}, 'Opprett ressurs')}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
